// react
import React, { useEffect, useState } from "react";

import { faCheckCircle } from "@fortawesome/pro-duotone-svg-icons";
import { faTimes } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Avatar } from "@performyard/py-components";

import Button from "../../../components/Button";
// components
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";

// dependencies
import PropTypes from "../../../lib/prop-types";

// styles
import css from "./LogDetails.module.scss";

const BulkDeactivateModal = (props) => {
	const {
		handleClose,
		numUsersToDeactivate,
		handleDeactivate,
		handleDismiss,
		displayDeactivate,
		dismissingUsers,
		deactivatingUsers,
		employeeData = {},
		getEmployeeData,
		employeesRequiringApprovalToDeactivate,
		getEmployeesRequiringApprovalToDeactivate,
	} = props;

	const [actionSelected, setActionSelected] = useState(false);
	const [dismissed, setDismissed] = useState(false);
	const [failed, setFailed] = useState(null);

	const showActionButtons = !displayDeactivate && !actionSelected;

	useEffect(() => {
		getEmployeeData();
		getEmployeesRequiringApprovalToDeactivate();
	}, []);

	const deactivateUsers = async () => {
		const response = await handleDeactivate();
		const result = response?.data ? response.data.getIn(["count", "response"], "") : null;
		// lookup user names

		if (result !== "202") {
			const failedMessage = (
				<div className={css.modalMessageContainer}>
					<div className={css.modalHeading}>
						<div className={`${css.resultIcon} ${css.failed}`}>
							<FontAwesomeIcon icon={faTimes} />
						</div>
						<div>Could not deactivate users.</div>
					</div>
				</div>
			);

			setFailed(failedMessage);
		}
		setActionSelected(true);
	};

	const dismissDeactivate = async () => {
		const response = await handleDismiss();
		const result = response?.data ? response.data.getIn(["count", "status"], "") : null;

		if (result === "200") {
			setDismissed(true);
		} else {
			const failedMessage = (
				<div className={css.modalMessageContainer}>
					<div className={css.modalHeading}>
						<div className={`${css.resultIcon} ${css.failed}`}>
							<FontAwesomeIcon icon={faTimes} />
						</div>
						<div>Could not dismiss deactivation</div>
					</div>
				</div>
			);
			setFailed(failedMessage);
		}

		setActionSelected(true);
	};

	const content = (
		<div className={css.deactivateUsersModalMessage}>
			{displayDeactivate ? (
				<div className={css.modalMessageContainer}>
					{failed === null ? (
						<div className={css.modalHeading}>
							<div className={`${css.resultIcon} ${css.success}`}>
								<FontAwesomeIcon icon={faCheckCircle} />
							</div>
							<div>{`Successfully deactivated ${numUsersToDeactivate} user${
								numUsersToDeactivate > 1 ? "s" : ""
							}:`}</div>
						</div>
					) : null}
					{employeeData?.users ? (
						<div className={css.deactivationContainer}>
							{employeeData.users.map((user) => (
								<div className={css.deactivationUserBox} key={user._id}>
									<Avatar size={24} dangerouslySetClassName={css.userAvatar} id={user._id} />
									<div className={css.userName}>{user.name}</div>
								</div>
							))}
						</div>
					) : (
						<Spinner size={24} centered={true} />
					)}
				</div>
			) : null}
			{!displayDeactivate && !dismissed && failed === null ? (
				<div>
					<div className={css.modalHeading}>
						<div>{`Are you sure you want to deactivate ${numUsersToDeactivate} user${
							numUsersToDeactivate > 1 ? "s" : ""
						}?`}</div>
					</div>
					{employeesRequiringApprovalToDeactivate?.users?.length > 0 ? (
						<div className={css.deactivationContainer}>
							{employeesRequiringApprovalToDeactivate.users.map((user) => (
								<div className={css.deactivationUserBox} key={user._id}>
									<Avatar size={24} dangerouslySetClassName={css.userAvatar} id={user._id} />
									<div className={css.userName}>{user.name}</div>
								</div>
							))}
						</div>
					) : null}
				</div>
			) : null}
			{dismissed && failed === null ? (
				<div className={css.modalMessageContainer}>
					<div className={css.modalHeading}>
						<div className={`${css.resultIcon} ${css.success}`}>
							<FontAwesomeIcon icon={faCheckCircle} />
						</div>
						<div>Deactivation Dismissed</div>
					</div>
				</div>
			) : null}
			{failed !== null ? failed : null}
		</div>
	);

	const buttons = (
		<>
			{showActionButtons ? (
				<>
					<Button
						style={{ marginRight: 8 }}
						size="large"
						color="grey"
						onClick={dismissDeactivate}
						isRequesting={dismissingUsers}
					>
						Dismiss
					</Button>
					<Button size="large" color="red" onClick={deactivateUsers} isRequesting={deactivatingUsers}>
						Deactivate
					</Button>
				</>
			) : (
				<Button size="large" color="blue" onClick={handleClose}>
					Close
				</Button>
			)}
		</>
	);
	return <Modal content={content} buttons={buttons} handleClose={handleClose} />;
};

BulkDeactivateModal.propTypes = {
	numUsersToDeactivate: PropTypes.number.isRequired,
	handleDeactivate: PropTypes.func.isRequired,
	handleDismiss: PropTypes.func.isRequired,
	handleClose: PropTypes.func.isRequired,
	deactivatingUsers: PropTypes.bool.isRequired,
	dismissingUsers: PropTypes.bool.isRequired,
	displayDeactivate: PropTypes.bool.isRequired,
	employeeData: PropTypes.object,
	getEmployeeData: PropTypes.func.isRequired,
	employeesRequiringApprovalToDeactivate: PropTypes.object,
	getEmployeesRequiringApprovalToDeactivate: PropTypes.func.isRequired,
};

export default BulkDeactivateModal;
